/**
 * Managing a campaign from its card (A7, M2 of `audits/2026-10-21/campaign-manage-spec.md`):
 * the decisions behind Edit, Delete and Remove, where a runner can reach them. The
 * card (`components/CampaignCard.tsx`) renders these and decides nothing.
 *
 * 🔴 **Only what changed is sent.** The worker's two PUTs send only the fields given
 * (docs/API-CONTRACT.md, `PUT /api/v1/campaigns/:id` and `PUT /api/v1/line-items/:id`), so
 * a form that sent every field would write a budget nobody touched — and a write of a
 * field X then reports differently is a 502 the operator has to read. A field left as it
 * was is not sent; a form with nothing changed is refused here, before anything is asked.
 *
 * 🔴 **An empty field means «leave it», never «unset it».** The worker takes a number or
 * a non-empty string and nothing else; there is no way through v1 to clear a budget or a
 * name, and sending `0` for a cleared budget would be this console inventing a figure.
 *
 * Money goes through money.ts (`amountToMicro`, `microAmount`): no arithmetic here. Times
 * are the wizard's: `localInputValue` for the field, `utcIso` for the wire.
 */
import type { Campaign, CampaignUpdate, LineItem, LineItemUpdate } from "./api.ts";
import { localInputValue, utcIso } from "./create-ad.ts";
import { amountToMicro, microAmount } from "./money.ts";

const record = (body: unknown): Record<string, unknown> =>
	typeof body === "object" && body !== null && !Array.isArray(body)
		? (body as Record<string, unknown>)
		: {};

const errorOf = (b: Record<string, unknown>, status: number): string =>
	typeof b.error === "string" && b.error !== "" ? b.error : `HTTP ${status}`;

// ── The campaign's edit ───────────────────────────────────────────────

/** The three fields as typed: amounts in the instrument's currency, with up to two decimals. */
export interface CampaignEdit {
	name: string;
	daily: string;
	total: string;
}

/** What the form starts from: X's figures, through money.ts; an absent budget is an empty field. */
export function campaignEdit(
	c: Pick<Campaign, "name" | "dailyBudgetAmountLocalMicro" | "totalBudgetAmountLocalMicro">,
): CampaignEdit {
	return {
		name: c.name,
		daily: c.dailyBudgetAmountLocalMicro === null ? "" : microAmount(c.dailyBudgetAmountLocalMicro),
		total: c.totalBudgetAmountLocalMicro === null ? "" : microAmount(c.totalBudgetAmountLocalMicro),
	};
}

/**
 * The card's edit state when it mounts: the form, when the card was opened by the row's
 * Edit on an account this reader can write; nothing otherwise. 🔴 `writable` is checked
 * here as well as on the row, so a card handed `opening` without the right to write
 * still starts reading — the row hides controls, it grants nothing.
 */
export function openingEdit(
	c: Pick<Campaign, "name" | "dailyBudgetAmountLocalMicro" | "totalBudgetAmountLocalMicro">,
	opening: boolean,
	writable: boolean,
): CampaignEdit | null {
	return opening && writable ? campaignEdit(c) : null;
}

/**
 * 🔴 Whether the campaign's own daily budget is offered on the card's Edit: not under
 * budget_optimization LINE_ITEM. On the create X refused the pair — «Please remove your
 * daily campaign budget before turning on campaign budget optimization» (-3d, A9, request
 * 6e24d969badff983, 2026-10-23); nobody has sent one on the edit, and this console does not
 * offer a request it has seen refused. A campaign whose answer carries no value is not
 * LINE_ITEM, and keeps the field.
 */
export function campaignDailyOffered(c: { budgetOptimization?: string | null }): boolean {
	return c.budgetOptimization !== "LINE_ITEM";
}

export type CampaignEditProblem = "name" | "daily-invalid" | "total-invalid" | "nothing-changed";

/**
 * The PUT body, or why none should be sent. A changed name is sent trimmed and must not
 * be blank (the worker refuses a blank one; its length is X's to refuse — «Maximum length:
 * 255 characters»). A budget is sent when its micro figure differs from X's; an empty
 * field is left alone. 🔴 Nothing here compares daily with total: the reference says
 * daily «should be» within total, the worker does not refuse it, and X arbitrates — as on
 * the create (spec rev. 2 §4). Under LINE_ITEM the daily is never sent, whatever the
 * field holds (`campaignDailyOffered`).
 */
export function campaignUpdate(
	account: string,
	c: Pick<
		Campaign,
		"name" | "dailyBudgetAmountLocalMicro" | "totalBudgetAmountLocalMicro" | "budgetOptimization"
	>,
	edit: CampaignEdit,
): { ok: true; value: CampaignUpdate } | { ok: false; problem: CampaignEditProblem } {
	const value: CampaignUpdate = { account };
	const name = edit.name.trim();
	if (name !== c.name) {
		if (name === "") return { ok: false, problem: "name" };
		value.name = name;
	}
	for (const [key, text, current] of [
		["dailyBudgetAmountLocalMicro", edit.daily, c.dailyBudgetAmountLocalMicro],
		["totalBudgetAmountLocalMicro", edit.total, c.totalBudgetAmountLocalMicro],
	] as const) {
		if (text.trim() === "") continue;
		if (key === "dailyBudgetAmountLocalMicro" && !campaignDailyOffered(c)) continue;
		const micro = amountToMicro(text);
		if (micro === null) {
			return {
				ok: false,
				problem: key === "dailyBudgetAmountLocalMicro" ? "daily-invalid" : "total-invalid",
			};
		}
		if (micro !== current) value[key] = micro;
	}
	if (Object.keys(value).length === 1) return { ok: false, problem: "nothing-changed" };
	return { ok: true, value };
}

// ── The line item's edit ──────────────────────────────────────────────

/**
 * The four fields as typed; the times are `<input type="datetime-local">` values, the total
 * an amount in the instrument's currency with up to two decimals.
 */
export interface LineItemEdit {
	name: string;
	start: string;
	end: string;
	total: string;
	/** X's daily budget; the card offers the field only under a LINE_ITEM campaign (`dailyEditable`). */
	daily: string;
}

type LineItemEditable = Pick<
	LineItem,
	"name" | "startTime" | "endTime" | "totalBudgetAmountLocalMicro" | "dailyBudgetAmountLocalMicro"
>;

/**
 * Whether a line item's daily budget may be written under this campaign: X makes it «Only
 * available when budget_optimization is set to LINE_ITEM for the parent campaign», and the
 * worker reads the parent again before the write.
 */
export function dailyEditable(campaign: { budgetOptimization?: string | null }): boolean {
	return campaign.budgetOptimization === "LINE_ITEM";
}

/** What the form starts from: X's name (or nothing), X's times in the reader's clock, X's total. */
export function lineItemEdit(item: LineItemEditable): LineItemEdit {
	const local = (iso: string | null) => {
		if (iso === null) return "";
		const when = new Date(iso);
		return Number.isNaN(when.getTime()) ? "" : localInputValue(when);
	};
	return {
		name: item.name ?? "",
		start: local(item.startTime),
		end: local(item.endTime),
		total:
			item.totalBudgetAmountLocalMicro === null || item.totalBudgetAmountLocalMicro === undefined
				? ""
				: microAmount(item.totalBudgetAmountLocalMicro),
		daily:
			item.dailyBudgetAmountLocalMicro === null || item.dailyBudgetAmountLocalMicro === undefined
				? ""
				: microAmount(item.dailyBudgetAmountLocalMicro),
	};
}

export type LineItemEditProblem =
	| "name"
	| "start"
	| "end"
	| "end-before-start"
	| "total-invalid"
	| "daily-invalid"
	| "nothing-changed";

/**
 * The same instant, or not — to the minute. 🔴 X writes seconds and the field
 * (`datetime-local`) writes minutes, so a line item X holds at 09:00:30 fills the field as
 * 09:00 and an untouched field reads back as 09:00:00: compared to the second that is a
 * change nobody made, and a save would move X's time by thirty seconds. A field left as
 * it was is X's value to the minute; a minute's change is a change.
 */
function sameInstant(iso: string, held: string | null): boolean {
	if (held === null) return false;
	const a = Date.parse(iso);
	const b = Date.parse(held);
	return !Number.isNaN(a) && !Number.isNaN(b) && Math.floor(a / 60_000) === Math.floor(b / 60_000);
}

/**
 * The PUT body for the field form, or why none should be sent. 🔴 Never `status`: the
 * worker refuses a body carrying both, and the switch has its own button on the Line
 * items panel. A time left empty is left alone; a time given is sent in UTC (`utcIso`,
 * as the wizard sends it) when it differs from X's; the end must follow the start, with
 * X's own value standing in for a field not given. The total is sent when its micro figure
 * differs from X's, as the campaign's budgets are; an empty field is left alone.
 */
export function lineItemUpdate(
	account: string,
	item: LineItemEditable,
	edit: LineItemEdit,
): { ok: true; value: LineItemUpdate } | { ok: false; problem: LineItemEditProblem } {
	const value: LineItemUpdate = { account };
	const name = edit.name.trim();
	if (name !== (item.name ?? "")) {
		if (name === "") return { ok: false, problem: "name" };
		value.name = name;
	}
	let start = item.startTime;
	let end = item.endTime;
	if (edit.start.trim() !== "") {
		const iso = utcIso(edit.start);
		if (iso === null) return { ok: false, problem: "start" };
		if (!sameInstant(iso, item.startTime)) value.startTime = iso;
		start = iso;
	}
	if (edit.end.trim() !== "") {
		const iso = utcIso(edit.end);
		if (iso === null) return { ok: false, problem: "end" };
		if (!sameInstant(iso, item.endTime)) value.endTime = iso;
		end = iso;
	}
	if (start !== null && end !== null && Date.parse(end) <= Date.parse(start)) {
		return { ok: false, problem: "end-before-start" };
	}
	if (edit.total.trim() !== "") {
		const micro = amountToMicro(edit.total);
		if (micro === null) return { ok: false, problem: "total-invalid" };
		if (micro !== item.totalBudgetAmountLocalMicro) value.totalBudgetAmountLocalMicro = micro;
	}
	if ((edit.daily ?? "").trim() !== "") {
		const micro = amountToMicro(edit.daily);
		if (micro === null) return { ok: false, problem: "daily-invalid" };
		if (micro !== item.dailyBudgetAmountLocalMicro) value.dailyBudgetAmountLocalMicro = micro;
	}
	if (Object.keys(value).length === 1) return { ok: false, problem: "nothing-changed" };
	return { ok: true, value };
}

// ── Reading the answers ───────────────────────────────────────────────

/** One field X reports differently from what was sent, as the worker names it. */
/** `read` is absent — not null — when X's answer does not carry the field (the worker, step 6). */
export interface Mismatch {
	field: string;
	sent: unknown;
	read?: unknown;
}

export type UpdateOutcome =
	| { kind: "saved" }
	| { kind: "mismatch"; mismatch: Mismatch[]; error: string }
	| { kind: "refused"; status: number; error: string; rule: string | null };

/**
 * A PUT's answer. 200 is saved — and what was saved is the object the card reads again,
 * not this answer. 502 with `mismatch` is X having accepted the write and reporting
 * something else, field by field; the worker made no second attempt and neither does
 * this console. Anything else is a refusal, with the worker's `rule` when it named one
 * (`"immutable"`: the field is not in the reference's PUT at all).
 */
export function updateOutcome(status: number, body: unknown): UpdateOutcome {
	const b = record(body);
	if (status === 200) return { kind: "saved" };
	if (status === 502 && Array.isArray(b.mismatch)) {
		const mismatch = b.mismatch
			.filter((m): m is Record<string, unknown> => typeof m === "object" && m !== null)
			.map((m) => ({ field: String(m.field), sent: m.sent, ...("read" in m && { read: m.read }) }));
		return { kind: "mismatch", mismatch, error: errorOf(b, status) };
	}
	return {
		kind: "refused",
		status,
		error: errorOf(b, status),
		rule: typeof b.rule === "string" ? b.rule : null,
	};
}

export type DeleteOutcome =
	| {
			kind: "deleted";
			confirmed: "deleted" | "absent";
			/** The ids X listed under it afterwards, when the route lists (null when it does not, or the listing failed). */
			listed: string[] | null;
			/** The worker's sentence when the delete was confirmed and only the listing after it failed. */
			listingFailed: string | null;
	  }
	| {
			kind: "pause-first";
			entityStatus: string;
			error: string;
			/**
			 * A criterion's 409 is about its line item (since #235): the worker names it and the
			 * status X reported for it. Null on a campaign's or line item's 409, which is about itself.
			 */
			lineItemId: string | null;
	  }
	| { kind: "unknown"; error: string }
	| { kind: "disagreed"; error: string }
	| { kind: "refused"; status: number; error: string };

/**
 * A DELETE's answer, in the worker's five shapes. `deleted: true` with `confirmed` is gone
 * — on a 200, and on the 502 whose only failure was the listing afterwards, which still
 * carries both. 409 is «pause it first», this console's own rule. `deleted: "unknown"` is
 * retryable. A 502 without `deleted: true` is X answering the object alive — no second
 * attempt. `listKey` names the list the route answers (`lineItems` after a campaign,
 * `promotedTweets` after a line item, none after a criterion); the ids are kept and the
 * rows are not: what they ARE is the worker's reading, and the card re-reads anyway.
 */
export function deleteOutcome(
	status: number,
	body: unknown,
	listKey: "lineItems" | "promotedTweets" | null,
): DeleteOutcome {
	const b = record(body);
	if (status === 409) {
		return {
			kind: "pause-first",
			entityStatus: typeof b.entityStatus === "string" ? b.entityStatus : "ACTIVE",
			error: errorOf(b, status),
			lineItemId: typeof b.lineItemId === "string" ? b.lineItemId : null,
		};
	}
	if (b.deleted === "unknown") return { kind: "unknown", error: errorOf(b, status) };
	if (b.deleted === true && (b.confirmed === "deleted" || b.confirmed === "absent")) {
		const list = listKey === null ? undefined : b[listKey];
		const listed = Array.isArray(list)
			? list
					.map((row) => (typeof row === "object" && row !== null ? record(row).id : null))
					.filter((id): id is string => typeof id === "string")
			: null;
		return {
			kind: "deleted",
			confirmed: b.confirmed,
			listed,
			listingFailed: status === 502 ? errorOf(b, status) : null,
		};
	}
	if (status === 502) return { kind: "disagreed", error: errorOf(b, status) };
	return { kind: "refused", status, error: errorOf(b, status) };
}

/** The one state this console will not delete from: the worker answers 409 for it, and the card says so before asking. */
export function deletable(entityStatus: string): boolean {
	return entityStatus !== "ACTIVE";
}
